import { type Theme } from '#app/theme/index.js';
import { type TextInputValue, type TextInputVariant } from './types.js';
import { ReactNode } from 'react';
import { css, styled } from 'styled-components';

const getColor = ({
  disabled,
  hasError,
  theme,
}: {
  disabled: boolean;
  hasError: boolean;
  theme: Theme;
}): string => {
  if (disabled) {
    return theme.colorsV2.gray40;
  }

  if (hasError) {
    return theme.colorsV2.red50;
  }

  return theme.colorsV2.gray60;
};

const Container = styled.div<{ disabled: boolean; hasError: boolean }>(
  ({ disabled, hasError }) => {
    return css`
      ${({ theme }) => css`
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.15px;
        color: ${getColor({ disabled, hasError, theme })};
      `}
    `;
  },
);

const MaxCharacters = styled.span`
  margin-left: auto;
`;

type HelperTextProps<TVariant extends TextInputVariant> = {
  readonly disabled: boolean;
  readonly error?: null | string;
  readonly helperText?: null | ReactNode | string;
  readonly maxCharacters?: TVariant extends 'text' | 'textArea'
    ? number
    : never;
  readonly value?: TextInputValue<TVariant>;
  readonly variant: TVariant;
};

export const HelperText = <TVariant extends TextInputVariant>({
  disabled,
  error,
  helperText,
  maxCharacters,
  value,
  variant,
  ...props
}: HelperTextProps<TVariant>) => {
  const shownHelperText = error || helperText;
  const showHelperText = Boolean(shownHelperText) || Boolean(maxCharacters);

  if (!showHelperText) {
    return null;
  }

  return (
    <Container
      disabled={disabled}
      hasError={Boolean(error)}
      {...props}
    >
      {shownHelperText ? <span>{shownHelperText}</span> : null}
      {typeof maxCharacters === 'number' &&
      variant === 'text' &&
      typeof value === 'string' ? (
        <MaxCharacters>
          {value.length}/{maxCharacters}
        </MaxCharacters>
      ) : null}
    </Container>
  );
};
